import { Option, Schema } from "effect";

/**
 * Pure detectors for playground user sources (shared by runtime DO shim and
 * Monaco ambient GlobalProps generation). No Cloudflare runtime imports.
 */

/**
 * Find exported classes directly or transitively extending DurableObject in
 * source or bundled code. Handles `import { DurableObject as Alias }` renames plus both
 * `export class X extends …` and `class X …; export { X }` forms.
 *
 * A class whose base comes from a factory, such as an EffectActor base, has no
 * DurableObject ancestor in the text. The configuration names those classes in its
 * Durable Object bindings, so each `declared` name that the code exports counts too.
 */
export function detectDurableObjectClasses(code: string, declared: readonly string[] = []): string[] {
  const detected = new Set(inheritedDurableObjectClasses(code));
  if (declared.length === 0) return [...detected];
  const exportedNames = new Set<string>();
  for (const match of code.matchAll(/\bexport\s+(?:async\s+)?(?:class\s+|function\s*\*?\s*|(?:const|let|var)\s+)([\w$]+)/g)) {
    exportedNames.add(match[1]);
  }
  for (const exportMatch of code.matchAll(/export\s*\{([^}]*)\}/g)) {
    for (const segment of exportMatch[1].split(",")) {
      const spec = segment.trim().match(/^([\w$]+)(?:\s+as\s+([\w$]+))?$/);
      if (spec) exportedNames.add(spec[2] ?? spec[1]);
    }
  }
  for (const name of declared) if (exportedNames.has(name)) detected.add(name);
  return [...detected];
}

function inheritedDurableObjectClasses(code: string): string[] {
  const aliases = new Set<string>();
  for (const importMatch of code.matchAll(
    /import\s*\{([^}]*)\}\s*from\s*["']cloudflare:workers["']/g
  )) {
    for (const segment of importMatch[1].split(",")) {
      const alias = segment
        .trim()
        .match(/^DurableObject(?:\s+as\s+([\w$]+))?$/);
      if (alias) aliases.add(alias[1] ?? "DurableObject");
    }
  }
  if (aliases.size === 0) return [];

  const classBases = new Map<string, string>();
  // Both source form (`class X extends Base`) and esbuild's lowered form
  // (`var X = class [Name] extends Base`). Record the inheritance graph so
  // exported SQLite successors such as `SqliteLogSession extends LogSession`
  // are recognized transitively as Durable Objects.
  const classRe =
    /(?:class\s+([\w$]+)|(?:\b(?:var|let|const)\s+|[,;]\s*)([\w$]+)\s*=\s*class(?:\s+[\w$]+)?)\s+extends\s+([\w$]+)(?![\w$])/g;
  for (const classMatch of code.matchAll(classRe)) {
    classBases.set(classMatch[1] ?? classMatch[2], classMatch[3]);
  }

  const classNames = new Set<string>();
  let foundSubclass = true;
  while (foundSubclass) {
    foundSubclass = false;
    for (const [name, base] of classBases) {
      if (
        !classNames.has(name) &&
        (aliases.has(base) || classNames.has(base))
      ) {
        classNames.add(name);
        foundSubclass = true;
      }
    }
  }
  if (classNames.size === 0) return [];

  const exported = new Set<string>();
  for (const name of classNames) {
    // Escape metacharacters so identifiers like `$Session` or `Foo$Bar`
    // are matched literally (`$` is valid in JS identifiers but special in RegExp).
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    if (new RegExp(`export\\s+class\\s+${escaped}(?![\\w$])`).test(code)) {
      exported.add(name);
      continue;
    }
    for (const exportMatch of code.matchAll(/export\s*\{([^}]*)\}/g)) {
      for (const segment of exportMatch[1].split(",")) {
        const spec = segment
          .trim()
          .match(/^([\w$]+)(?:\s+as\s+([\w$]+))?$/);
        if (spec && spec[1] === name) exported.add(spec[2] ?? name);
      }
    }
  }
  return [...exported];
}

const isSourceText = /* @__PURE__ */ (() => Schema.is(Schema.String))();
const packageMain = /* @__PURE__ */ (() => Schema.decodeUnknownOption(Schema.fromJsonString(Schema.Struct({ main: Schema.String }))))();

/** Collect unique DO class names across a playground file map. */
export function detectDurableObjectClassesInFiles(
  files: Record<string, string>
): string[] {
  const names = new Set<string>();
  for (const contents of Object.values(files)) {
    if (!isSourceText(contents)) continue;
    for (const name of detectDurableObjectClasses(contents)) {
      names.add(name);
    }
  }
  return [...names].sort();
}

/**
 * Resolve the worker entry module path for typeof import(...) in ambient types.
 * Mirrors runtime defaults (package.json main / cf-worker import / common names).
 */
export function resolvePlaygroundEntry(
  files: Record<string, string>
): string | null {
  const pkg = Option.getOrUndefined(packageMain(files["package.json"] ?? "{}"));
  if (pkg !== undefined) {
    const main = pkg.main.replace(/\\/g, "/").replace(/^\.\//, "");
    if (files[main] !== undefined) return main;
  }

  const config =
    files["cloudflare.config.ts"] ??
    files["cloudflare.config.js"] ??
    files["cloudflare.config.mts"] ??
    "";
  // import * as entrypoint from "./src/index.ts" with { type: "cf-worker" }
  const cfWorkerImport = config.match(
    /from\s*["'](\.\/[^"']+)["']\s*with\s*\{\s*type:\s*["']cf-worker["']/
  );
  if (cfWorkerImport) {
    const path = cfWorkerImport[1].replace(/^\.\//, "");
    if (files[path] !== undefined) return path;
  }
  const entryField = config.match(
    /\bentry(?:point)?\s*:\s*["']([^"']+)["']/
  );
  if (entryField) {
    const path = entryField[1].replace(/^\.\//, "");
    if (files[path] !== undefined) return path;
  }

  for (const candidate of [
    "src/index.ts",
    "src/index.tsx",
    "src/index.js",
    "src/index.jsx",
    "index.ts",
    "index.tsx",
    "index.js"
  ]) {
    if (files[candidate] !== undefined) return candidate;
  }

  const first = Object.keys(files)
    .filter(
      (f) =>
        /\.(ts|tsx|js|jsx|mjs)$/i.test(f) &&
        !/(^|\/)(cloudflare|wrangler|vite)\.config\./i.test(f)
    )
    .sort()[0];
  return first ?? null;
}
